/**
 * VHAKACHA BOOKING SCHEMA  (web app)
 * ============================================================
 * Mirror of  provider app → src/lib/booking.ts, plus the WRITE path
 * (`buildBooking`) since the web app is the only booking author.
 *
 * The v2 contract, and why each field exists:
 *
 *   serviceProviderId  the provider dashboard queries on this. Legacy
 *                      documents had no such field, so
 *                      `where('serviceProviderId','==',…)` matched
 *                      nothing and providers saw zero bookings.
 *   listingId          legacy stored the LISTING id under a key named
 *                      `serviceProvider`, which read as the provider.
 *   itineraryId        legacy bookings and itineraries were unlinked;
 *                      each held its own copy of a reservation.
 *   money fields       legacy stored no total at all, while the
 *                      provider UI rendered Net Payout / Total Paid.
 *   status             UPPERCASE, matching the provider UI comparisons.
 *   dates              Firestore Timestamps, not locale strings.
 * ============================================================
 */

import { Timestamp } from 'firebase/firestore';
import {
  computeBookingTotals,
  normalizeTravelers,
  nightsBetween,
  defaultPriceUnitFor,
  DEFAULT_CURRENCY,
} from './pricing';

export const BOOKING_SCHEMA_VERSION = 2;

/**
 * MIRRORED — must match BOOKING_STATUSES in the provider repo's
 * src/lib/booking.ts. An unknown status falls through normalizeStatus() to
 * 'PENDING', so a value this list is missing does not merely fail to render:
 * it silently reports the wrong state. A declined booking would have shown
 * the traveller "pending" indefinitely.
 *
 * DECLINED and CANCELLED are distinct on purpose. DECLINED is a request the
 * operator never accepted — nothing promised, nothing owed. CANCELLED is an
 * accepted booking called off afterwards, which carries a refund question.
 */
export const BOOKING_STATUSES = ['PENDING', 'CONFIRMED', 'DECLINED', 'REDEEMED', 'CANCELLED'];

/** Legacy rows stored lowercase "pending", which failed every === 'PENDING'. */
export function normalizeStatus(raw) {
  const s = String(raw == null ? '' : raw).trim().toUpperCase();
  if (BOOKING_STATUSES.includes(s)) return s;
  if (s === 'COMPLETE' || s === 'COMPLETED') return 'REDEEMED';
  if (s === 'CANCELED') return 'CANCELLED';
  return 'PENDING';
}

/**
 * Accepts a Firestore Timestamp, a JS Date, an ISO string, epoch millis,
 * or the legacy `dateBooked` locale string "DD/MM/YYYY, HH:mm:ss".
 */
export function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  if (typeof value === 'object') {
    if (typeof value.toDate === 'function') {
      const d = value.toDate();
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const secs = value.seconds != null ? value.seconds : value._seconds;
    if (typeof secs === 'number') return new Date(secs * 1000);
  }

  if (typeof value === 'number') return new Date(value);

  if (typeof value === 'string') {
    const legacy = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:,\s*(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
    if (legacy) {
      const [, dd, mm, yyyy, hh = '0', mi = '0', ss = '0'] = legacy;
      return new Date(+yyyy, +mm - 1, +dd, +hh, +mi, +ss);
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
}

export function toTimestamp(value) {
  const d = toDate(value);
  return d ? Timestamp.fromDate(d) : null;
}

/**
 * WRITE PATH — builds a canonical v2 booking document.
 *
 * `listing` is a listing document from the `listings` collection (the
 * web app calls these "serviceProvider" in older components, which is
 * exactly the naming confusion this schema fixes).
 */
export function buildBooking({
  id,
  listing,
  travelers,
  dateRange,
  info,
  user,
  itineraryId = null,
  commissionRate,
}) {
  if (!listing) throw new Error('buildBooking: a listing is required');
  if (!listing.id) throw new Error('buildBooking: listing.id is required');

  const cleanTravelers = normalizeTravelers(travelers);

  const start = toDate(dateRange && dateRange[0]) || new Date();
  const end = toDate(dateRange && dateRange[1]) || start;
  const nights = nightsBetween(start, end);

  const priceUnit = listing.priceUnit || defaultPriceUnitFor(listing.type);
  const currency = listing.currency || DEFAULT_CURRENCY;

  const totals = computeBookingTotals({
    unitCost: Number(listing.cost) || 0,
    priceUnit,
    currency,
    nights,
    travelers: cleanTravelers,
    commissionRate,
  });

  return {
    id,
    schemaVersion: BOOKING_SCHEMA_VERSION,

    // ── Ownership / joins ──────────────────────────────────
    // serviceProviderId is what the provider dashboard filters on.
    serviceProviderId: listing.serviceProviderId || (listing.company && listing.company.id) || null,
    listingId: listing.id,
    itineraryId,
    uid: (user && user.uid) || null,

    // ── Customer ───────────────────────────────────────────
    customerName: (user && (user.displayName || user.email)) || 'Traveler',
    customerContact: (user && user.email) || '',

    // ── Denormalized listing snapshot ──────────────────────
    // Named `listing` (not `serviceProvider`) so the key matches what
    // it actually holds. Images are copied through — the legacy writer
    // hardcoded `images: []` and lost them.
    listing: {
      id: listing.id,
      name: listing.name || '',
      type: listing.type || '',
      location: listing.location || '',
      cost: Number(listing.cost) || 0,
      currency,
      priceUnit,
      images: Array.isArray(listing.images) ? listing.images.slice(0, 3) : [],
      email: (listing.contact && listing.contact.email) || '',
      phone: (listing.contact && listing.contact.phone) || '',
    },

    // ── What / when ────────────────────────────────────────
    // bookingType carries the real category. The legacy writer
    // hardcoded "accommodation", so a sunset cruise was filed as one.
    bookingType: listing.type || 'accommodation',
    dates: [Timestamp.fromDate(start), Timestamp.fromDate(end)],
    startDate: Timestamp.fromDate(start),
    endDate: Timestamp.fromDate(end),
    nights,
    travelers: cleanTravelers,
    guests: cleanTravelers.adults + cleanTravelers.children + cleanTravelers.infants,

    // ── Money ──────────────────────────────────────────────
    currency: totals.currency,
    unitCost: totals.unitCost,
    priceUnit: totals.priceUnit,
    quantity: totals.quantity,
    subtotal: totals.subtotal,
    commissionRate: totals.commissionRate,
    commissionDeducted: totals.commissionDeducted,
    netMerchantPay: totals.netMerchantPay,
    totalPaid: totals.totalPaid,

    // ── Lifecycle ──────────────────────────────────────────
    status: 'PENDING',
    info: typeof info === 'string' ? info : '',
    createdAt: Timestamp.fromDate(new Date()),
    updatedAt: Timestamp.fromDate(new Date()),
  };
}

/**
 * READ PATH — accepts either schema and returns one view model, so
 * pages never branch on version.
 */
export function normalizeBooking(raw) {
  if (!raw) return null;

  const isV2 = Number(raw.schemaVersion) >= 2;
  const snapshot = raw.listing || raw.serviceProvider || {};

  const travelers = normalizeTravelers(raw.travelers);
  const dates = Array.isArray(raw.dates) ? raw.dates : [];
  const startDate = toDate(raw.startDate || dates[0]);
  const endDate = toDate(raw.endDate || dates[1] || dates[0]);

  const listingType = snapshot.type || raw.bookingType || raw.type || '';
  const priceUnit = raw.priceUnit || snapshot.priceUnit || defaultPriceUnitFor(listingType);
  const currency = raw.currency || snapshot.currency || DEFAULT_CURRENCY;
  const unitCost = Number(raw.unitCost != null ? raw.unitCost : snapshot.cost) || 0;
  const nights =
    Number.isFinite(raw.nights) && raw.nights > 0 ? Math.floor(raw.nights) : nightsBetween(startDate, endDate);

  // v2 carries authoritative money; legacy rows get it recomputed from
  // the denormalized cost so historical bookings still show a total.
  const totals =
    isV2 && Number.isFinite(raw.subtotal)
      ? {
          quantity: Number(raw.quantity) || 1,
          subtotal: Number(raw.subtotal) || 0,
          commissionRate: Number(raw.commissionRate) || 0,
          commissionDeducted: Number(raw.commissionDeducted) || 0,
          netMerchantPay: Number(raw.netMerchantPay) || 0,
          totalPaid: Number(raw.totalPaid) || 0,
        }
      : computeBookingTotals({ unitCost, priceUnit, currency, nights, travelers });

  return {
    id: raw.id,
    schemaVersion: isV2 ? Number(raw.schemaVersion) : 1,
    isLegacy: !isV2,

    serviceProviderId: raw.serviceProviderId || snapshot.serviceProviderId || null,
    listingId: raw.listingId || snapshot.id || null,
    itineraryId: raw.itineraryId || null,
    uid: raw.uid || null,

    customerName: raw.customerName || 'Traveler',
    customerContact: raw.customerContact || '',

    listingName: snapshot.name || 'Unknown listing',
    listingType,
    location: snapshot.location || '',
    images: Array.isArray(snapshot.images) ? snapshot.images : [],
    contactEmail: snapshot.email || (snapshot.contact && snapshot.contact.email) || '',

    bookingType: raw.bookingType || listingType || 'accommodation',
    startDate,
    endDate,
    nights,
    travelers,
    guests: travelers.adults + travelers.children + travelers.infants,

    currency,
    unitCost,
    priceUnit,
    quantity: totals.quantity,
    subtotal: totals.subtotal,
    commissionRate: totals.commissionRate,
    commissionDeducted: totals.commissionDeducted,
    netMerchantPay: totals.netMerchantPay,
    totalPaid: totals.totalPaid,

    status: normalizeStatus(raw.status),
    info: raw.info || '',
    createdAt: toDate(raw.createdAt || raw.dateBooked),
    updatedAt: toDate(raw.updatedAt),
  };
}
